/**
 * Clarity event taxonomy + finite-cardinality helpers.
 *
 * Event names are lowercase kebab-case and STABLE (renaming breaks historical funnels).
 * The `ClarityEvent` union is the single source of truth — trackClarityEvent only accepts it.
 * Buckets/normalizers here keep tag values low-cardinality (no raw ids, timings, or URLs).
 */

export type ClarityEvent =
  // Acquisition / public funnel (anonymous, Phase 0.5)
  | 'landing-viewed'
  | 'pricing-viewed'
  | 'plan-compared'
  | 'demo-watched'
  | 'cta-clicked'
  | 'identity-stitched'
  // Auth
  | 'auth-login-started'
  | 'auth-login-succeeded'
  | 'auth-login-failed'
  | 'auth-logout-clicked'
  | 'auth-session-expired'
  | 'auth-password-reset-started'
  | 'auth-password-reset-completed'
  | 'auth-first-login-password-required'
  | 'auth-password-changed'
  // Onboarding / signup
  | 'signup-started'
  | 'signup-submitted'
  | 'signup-failed'
  | 'verify-code-viewed'
  | 'verify-code-submitted'
  | 'verify-code-failed'
  | 'verify-succeeded'
  | 'onboarding-started'
  | 'onboarding-step-viewed'
  | 'onboarding-step-completed'
  | 'onboarding-completed'
  | 'onboarding-abandoned'
  // Navigation / gates
  | 'route-viewed'
  | 'protected-route-blocked'
  | 'role-gate-hit'
  | 'paywall-hit'
  | 'module-locked-viewed'
  // Journeys (generic)
  | 'journey-started'
  | 'journey-step-viewed'
  | 'journey-step-completed'
  | 'journey-completed'
  | 'journey-abandoned'
  | 'journey-failed'
  // Forms / states
  | 'form-submitted'
  | 'form-validation-failed'
  | 'empty-state-viewed'
  | 'loading-state-long'
  | 'retry-clicked'
  // CRUD
  | 'entity-create-started'
  | 'entity-create-succeeded'
  | 'entity-create-failed'
  | 'entity-update-started'
  | 'entity-update-succeeded'
  | 'entity-update-failed'
  | 'entity-delete-started'
  | 'entity-delete-succeeded'
  | 'entity-delete-failed'
  // API / network
  | 'api-request-failed'
  | 'api-unauthorized'
  | 'api-forbidden'
  | 'api-rate-limited'
  | 'api-validation-error'
  | 'api-server-error'
  | 'api-timeout'
  | 'api-retry-succeeded'
  // Performance
  | 'performance-slow-route'
  | 'performance-slow-api'
  | 'performance-slow-interaction'
  | 'performance-long-task'
  | 'performance-poor-web-vital'
  // Errors
  | 'ui-error-boundary-hit'
  | 'uncaught-error'
  | 'unhandled-promise-rejection'
  | 'chunk-load-failed'
  | 'unexpected-empty-data'
  // Billing / paywall
  | 'billing-page-viewed'
  | 'plan-viewed'
  | 'subscription-required'
  | 'free-tier-limit-hit'
  // Self-serve checkout (billing page)
  | 'billing-viewed'
  | 'billing-checkout-started'
  | 'billing-checkout-unavailable'
  | 'billing-return-success'
  | 'billing-return-failed'
  // Admin / RBAC
  | 'admin-page-viewed'
  | 'permission-denied'
  | 'tenant-switch'
  | 'impersonation-started'
  | 'impersonation-ended';

// ── Duration buckets (ms → label) ────────────────────────────────────────────
export type DurationBucket = 'lt_1s' | '1_3s' | '3_10s' | '10_30s' | '30_60s' | 'gt_60s';

export function durationBucket(ms: number): DurationBucket {
  if (ms < 1000) return 'lt_1s';
  if (ms < 3000) return '1_3s';
  if (ms < 10000) return '3_10s';
  if (ms < 30000) return '10_30s';
  if (ms < 60000) return '30_60s';
  return 'gt_60s';
}

// ── Performance buckets ──────────────────────────────────────────────────────
export type PerfBucket = 'fast' | 'ok' | 'slow' | 'very_slow';

/** Generic latency rating. `slowMs` marks the start of "slow"; 3× that is "very_slow". */
export function perfBucket(ms: number, slowMs = 2000): PerfBucket {
  if (ms < slowMs / 2) return 'fast';
  if (ms < slowMs) return 'ok';
  if (ms < slowMs * 3) return 'slow';
  return 'very_slow';
}

// ── HTTP → event + health bucket ─────────────────────────────────────────────
export type ApiHealthBucket = 'ok' | 'client_error' | 'server_error' | 'network' | 'timeout';

export interface ApiFailureKind {
  event: ClarityEvent;
  health: ApiHealthBucket;
}

/** Map an HTTP status (or 0/undefined for no-response) + a timeout flag to a safe event. */
export function classifyApiFailure(status: number | undefined, isTimeout = false): ApiFailureKind {
  if (isTimeout) return { event: 'api-timeout', health: 'timeout' };
  if (!status) return { event: 'api-request-failed', health: 'network' };
  if (status === 401) return { event: 'api-unauthorized', health: 'client_error' };
  if (status === 403) return { event: 'api-forbidden', health: 'client_error' };
  if (status === 408) return { event: 'api-timeout', health: 'timeout' };
  if (status === 429) return { event: 'api-rate-limited', health: 'client_error' };
  if (status === 400 || status === 422) return { event: 'api-validation-error', health: 'client_error' };
  if (status >= 500) return { event: 'api-server-error', health: 'server_error' };
  if (status >= 400) return { event: 'api-request-failed', health: 'client_error' };
  return { event: 'api-request-failed', health: 'ok' };
}

// ── Endpoint normalization (strip ids + query → low cardinality) ──────────────
const ID_SEGMENT_RE = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,})$/i;

/** `/contacts/123?x=1` → `/contacts/:id`. Never returns query strings or raw ids. */
export function normalizeEndpoint(rawPath: string): string {
  const path = rawPath.split('?')[0].split('#')[0];
  const segments = path.split('/').map((seg) => (ID_SEGMENT_RE.test(seg) ? ':id' : seg));
  return segments.join('/') || '/';
}

// ── Route → group / page-type (finite enums) ─────────────────────────────────
export type RouteGroup =
  | 'public'
  | 'auth'
  | 'dashboard'
  | 'finance'
  | 'inventory'
  | 'pos'
  | 'hr'
  | 'projects'
  | 'account'
  | 'other';

export type PageType = 'marketing' | 'auth' | 'dashboard' | 'detail' | 'terminal' | 'list' | 'other';

const PUBLIC_PATHS = new Set(['/', '/pricing', '/about', '/contact', '/security', '/privacy', '/terms', '/resources']);
const AUTH_PATHS = new Set(['/login', '/signup', '/verify', '/impersonate', '/change-password']);

export function routeGroup(pathname: string): RouteGroup {
  if (PUBLIC_PATHS.has(pathname) || pathname.startsWith('/resources/')) return 'public';
  if (AUTH_PATHS.has(pathname)) return 'auth';
  if (pathname === '/dashboard') return 'dashboard';
  if (/^\/(khata|invoices|contacts|partners|expenses|reports|financial-dashboard|cashbook|vat-report)/.test(pathname)) {
    return 'finance';
  }
  if (/^\/(inventory|damaged-goods|demand-forecast|purchase)/.test(pathname)) return 'inventory';
  if (/^\/pos/.test(pathname)) return 'pos';
  if (/^\/hr/.test(pathname)) return 'hr';
  if (/^\/(projects|timesheets)/.test(pathname)) return 'projects';
  if (/^\/(team|settings|business-profile)/.test(pathname)) return 'account';
  return 'other';
}

export function pageType(pathname: string, group: RouteGroup = routeGroup(pathname)): PageType {
  if (group === 'public') return 'marketing';
  if (group === 'auth') return 'auth';
  if (pathname === '/dashboard' || pathname === '/financial-dashboard') return 'dashboard';
  if (pathname === '/pos/terminal') return 'terminal';
  if (/\/[0-9a-f-]{6,}$/i.test(pathname) || /\/\d+$/.test(pathname)) return 'detail';
  return 'list';
}

// ── Role number → stable label (matches src/store/useAuthStore.ts ROLE_MAP) ──
export type RoleLabel =
  | 'superadmin'
  | 'admin'
  | 'accountant'
  | 'sales'
  | 'pos_operator'
  | 'owner'
  | 'hr_manager'
  | 'unknown';

const ROLE_LABELS: Record<number, RoleLabel> = {
  0: 'superadmin',
  1: 'admin',
  2: 'accountant',
  3: 'sales',
  4: 'pos_operator',
  5: 'owner',
  6: 'hr_manager',
};

export function roleLabel(role: number | null | undefined): RoleLabel {
  return (role !== null && role !== undefined && ROLE_LABELS[role]) || 'unknown';
}
